// src/pages/ClassTimetable.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { ref, get } from 'firebase/database';

const ClassTimetable = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classTimetable, setClassTimetable] = useState(null);
  const [allTimetables, setAllTimetables] = useState({});
  const [debugInfo, setDebugInfo] = useState('');
  const [foundEntries, setFoundEntries] = useState(0);
  const [teachersMap, setTeachersMap] = useState({});

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const standardPeriods = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'];

  // Check admin authentication
  useEffect(() => {
    const isAdminLoggedIn = sessionStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/login');
    }
  }, [navigate]);

  // Load timetables and classes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Load timetables
        const timetablesSnap = await get(ref(db, 'timetables'));
        let timetablesData = {};
        let timetableKeys = [];

        if (timetablesSnap.exists()) {
          timetablesData = timetablesSnap.val();
          timetableKeys = Object.keys(timetablesData);
          console.log('📚 FULL TIMETABLE DATA:', timetablesData);
          console.log('📋 TIMETABLE KEYS:', timetableKeys);
          setAllTimetables(timetablesData);
        } else {
          console.log('⚠️ No timetables data found');
        }

        // Load teachers for resolving teacher IDs to names
        const teachersSnap = await get(ref(db, 'teachers'));
        if (teachersSnap.exists()) {
          const teachersData = teachersSnap.val();
          const teacherMap = {};
          Object.values(teachersData).forEach(teacher => {
            if (teacher.id) {
              teacherMap[teacher.id] = teacher.name || teacher.id;
            }
            if (teacher.name) {
              teacherMap[teacher.name] = teacher.name;
            }
          });
          setTeachersMap(teacherMap);
          console.log('👨‍🏫 TEACHERS MAP:', teacherMap);
        }

        // Load classes for additional class names
        const classesSnap = await get(ref(db, 'classes'));
        let classDataMap = {};
        if (classesSnap.exists()) {
          classDataMap = classesSnap.val();
          console.log('📚 CLASSES DATA:', classDataMap);
        }

        // Build final class list from timetables keys
        let finalClasses = [];

        // First priority: keys from timetables
        if (timetableKeys.length > 0) {
          finalClasses = timetableKeys.map(key => ({
            key: key,
            displayName: formatClassName(key)
          }));
        }

        // If no timetables, try to get classes from classes node
        if (finalClasses.length === 0 && classesSnap.exists()) {
          const classesData = classesSnap.val();
          Object.values(classesData).forEach(cls => {
            let classKey = null;
            let displayName = null;

            // Try to determine class key from various fields
            if (cls.name) {
              classKey = cls.name.replace(/ /g, '_');
              displayName = cls.name;
            } else if (cls.className) {
              classKey = cls.className.replace(/ /g, '_');
              displayName = cls.className;
            } else if (cls.class) {
              classKey = cls.class.replace(/ /g, '_');
              displayName = cls.class;
            }

            if (classKey && displayName) {
              // Add section if available
              if (cls.section) {
                displayName = `${displayName} - ${cls.section}`;
              }
              finalClasses.push({
                key: classKey,
                displayName: displayName
              });
            }
          });
        }

        // Remove duplicates (keep first occurrence)
        const seenKeys = new Set();
        finalClasses = finalClasses.filter(item => {
          if (seenKeys.has(item.key)) {
            return false;
          }
          seenKeys.add(item.key);
          return true;
        });

        console.log('🎯 FINAL DROPDOWN CLASSES:', finalClasses);
        setClasses(finalClasses);

        if (finalClasses.length === 0) {
          setDebugInfo('No classes with timetables found. Please create timetables first.');
        } else {
          setDebugInfo(`Found ${finalClasses.length} classes with timetables.`);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setDebugInfo('Error loading data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to format class name for display
  const formatClassName = (classKey) => {
    if (!classKey || typeof classKey !== 'string') {
      return String(classKey || 'Unknown Class');
    }
    
    // Convert 1_CRIMSON -> Class 1 - CRIMSON
    // Convert 2_INDIGO -> Class 2 - INDIGO
    const parts = classKey.split('_');
    if (parts.length === 2) {
      return `Class ${parts[0]} - ${parts[1]}`;
    }
    
    // If it doesn't have underscore, just replace underscores with spaces
    return classKey.replace(/_/g, ' ');
  };

  // Helper to get display class name for a class object
  const getDisplayClassName = (classItem) => {
    if (typeof classItem === 'string') {
      return formatClassName(classItem);
    }
    if (classItem && typeof classItem === 'object') {
      return classItem.displayName || formatClassName(classItem.key);
    }
    return 'Unknown Class';
  };

  // Helper to get class key from class item
  const getClassKey = (classItem) => {
    if (typeof classItem === 'string') {
      return classItem;
    }
    if (classItem && typeof classItem === 'object') {
      return classItem.key;
    }
    return String(classItem);
  };

  // Helper to get period data with flexible period key support
  const getPeriodData = (dayData, periodIndex) => {
    if (!dayData || typeof dayData !== 'object') {
      return null;
    }

    const periodNumber = periodIndex + 1;
    const possibleKeys = [
      `P${periodNumber}`,
      String(periodNumber),
      `Period${periodNumber}`,
      `period${periodNumber}`,
      `p${periodNumber}`,
      `period_${periodNumber}`,
      `P_${periodNumber}`
    ];

    for (const key of possibleKeys) {
      if (dayData[key] && typeof dayData[key] === 'object') {
        return { key: key, data: dayData[key] };
      }
    }

    return null;
  };

  // Helper to extract subject from period data
  const extractSubject = (periodData) => {
    if (!periodData || typeof periodData !== 'object') {
      return null;
    }

    // Try various field names
    if (periodData.subject) return periodData.subject;
    if (periodData.subjectName) return periodData.subjectName;
    if (periodData.subject_name) return periodData.subject_name;
    if (periodData.sub) return periodData.sub;
    if (periodData.Subject) return periodData.Subject;
    
    // If subject is an object with name
    if (periodData.subject && typeof periodData.subject === 'object') {
      return periodData.subject.name || periodData.subject.subjectName || null;
    }

    return null;
  };

  // Helper to extract teacher from period data
  const extractTeacher = (periodData) => {
    if (!periodData || typeof periodData !== 'object') {
      return null;
    }

    let teacherValue = null;

    // Try various field names
    if (periodData.teacher) teacherValue = periodData.teacher;
    else if (periodData.teacherName) teacherValue = periodData.teacherName;
    else if (periodData.teacher_name) teacherValue = periodData.teacher_name;
    else if (periodData.teacherId) teacherValue = periodData.teacherId;
    else if (periodData.teacherID) teacherValue = periodData.teacherID;
    else if (periodData.Teacher) teacherValue = periodData.Teacher;

    // If teacher is an object with name
    if (teacherValue && typeof teacherValue === 'object') {
      teacherValue = teacherValue.name || teacherValue.teacherName || teacherValue.id || null;
    }

    // If teacher is an ID, try to resolve to name
    if (teacherValue && typeof teacherValue === 'string' && teacherValue.startsWith('-')) {
      // It might be a Firebase ID
      if (teachersMap[teacherValue]) {
        return teachersMap[teacherValue];
      }
      // Try to find by ID in teachersMap values
      for (const [key, value] of Object.entries(teachersMap)) {
        if (value === teacherValue) {
          return key;
        }
      }
    }

    return teacherValue || 'Not Assigned';
  };

  // Load class timetable when class is selected
  useEffect(() => {
    if (!selectedClass) {
      setClassTimetable(null);
      setFoundEntries(0);
      setDebugInfo('');
      return;
    }

    console.log(`📖 Loading timetable for class: ${selectedClass}`);
    
    // Get the class key from selectedClass (handle both string and object)
    const classKey = getClassKey(selectedClass);
    
    // Get the timetable for the selected class
    const classData = allTimetables[classKey];
    
    if (classData && typeof classData === 'object') {
      console.log(`✅ Found data for class ${classKey}:`, classData);
      setClassTimetable(classData);
      
      // Count actual period entries
      let entryCount = 0;
      days.forEach(day => {
        const dayData = classData[day];
        if (dayData && typeof dayData === 'object') {
          standardPeriods.forEach((_, periodIndex) => {
            const periodResult = getPeriodData(dayData, periodIndex);
            if (periodResult && periodResult.data) {
              const subject = extractSubject(periodResult.data);
              const teacher = extractTeacher(periodResult.data);
              if (subject || teacher) {
                entryCount++;
              }
            }
          });
        }
      });
      
      setFoundEntries(entryCount);
      
      if (entryCount === 0) {
        setDebugInfo(`No timetable entries found for ${getDisplayClassName(selectedClass)}`);
      } else {
        setDebugInfo(`Found ${entryCount} timetable entries for ${getDisplayClassName(selectedClass)}`);
      }
    } else {
      console.log(`❌ No data found for class: ${classKey}`);
      setClassTimetable(null);
      setFoundEntries(0);
      setDebugInfo(`No timetable found for ${getDisplayClassName(selectedClass)}`);
    }
  }, [selectedClass, allTimetables]);

  // Get cell content for display
  const getCellContent = (day, periodIndex) => {
    if (!classTimetable || !classTimetable[day]) {
      return null;
    }

    const dayData = classTimetable[day];
    const periodResult = getPeriodData(dayData, periodIndex);
    
    if (!periodResult || !periodResult.data) {
      return null;
    }

    const subject = extractSubject(periodResult.data);
    const teacher = extractTeacher(periodResult.data);

    if (!subject && !teacher) {
      return null;
    }

    return {
      subject: subject || 'N/A',
      teacher: teacher || 'Not Assigned'
    };
  };

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem('isAdminLoggedIn');
    navigate('/login');
  };

  // Go back to dashboard
  const handleBack = () => {
    navigate('/admin');
  };

  // Print functionality
  const handlePrint = () => {
    const printContent = document.getElementById('class-print');
    if (!printContent) return;

    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.outerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // WhatsApp functionality
  const getWhatsAppMessage = () => {
    if (!classTimetable || !selectedClass) return '';

    const displayClassName = getDisplayClassName(selectedClass);

    let message = 'MIS INTERNATIONAL SCHOOL\n';
    message += 'CLASS TIMETABLE\n\n';
    message += `Class: ${displayClassName}\n`;
    message += '='.repeat(30) + '\n\n';

    days.forEach(day => {
      message += `${day.toUpperCase()}\n`;
      message += '-'.repeat(20) + '\n';
      
      let hasClasses = false;
      standardPeriods.forEach((period, periodIndex) => {
        const content = getCellContent(day, periodIndex);
        if (content) {
          message += `${period} - ${content.subject} - ${content.teacher}\n`;
          hasClasses = true;
        } else {
          message += `${period} - Free\n`;
        }
      });

      if (!hasClasses) {
        message += 'No classes scheduled\n';
      }
      message += '\n';
    });

    message += '='.repeat(30) + '\n';
    message += 'Generated by School Timetable System\n';

    
    return message;
  };

  const handleWhatsApp = (isBusiness = false) => {
    const message = getWhatsAppMessage();
    if (!message) return;

    const encodedMessage = encodeURIComponent(message);
    const url = isBusiness
      ? `https://api.whatsapp.com/send?phone=&text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(url, '_blank');
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={handleBack} style={styles.backBtn}>← Back</button>
          <h1 style={styles.headerTitle}>📚 Class Timetable</h1>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          🚪 Logout
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Controls */}
        <div style={styles.controls}>
          <div style={styles.selectorGroup}>
            <label style={styles.label}>Select Class</label>
            <select
              value={getClassKey(selectedClass)}
              onChange={(e) => {
                const selectedValue = e.target.value;
                // Find the class object or just use the value
                const classItem = classes.find(c => getClassKey(c) === selectedValue) || selectedValue;
                setSelectedClass(classItem);
              }}
              style={styles.select}
            >
              <option value="">-- Select a Class --</option>
              {Array.isArray(classes) && classes.map((classItem) => {
                const key = getClassKey(classItem);
                const display = getDisplayClassName(classItem);
                return (
                  <option key={key} value={key}>
                    {display}
                  </option>
                );
              })}
            </select>
          </div>

          {debugInfo && (
            <div style={styles.debugInfo}>
              ℹ️ {debugInfo}
            </div>
          )}

          {selectedClass && classTimetable && foundEntries > 0 && (
            <div style={styles.actionButtons}>
              <button onClick={handlePrint} style={styles.btnPrint}>
                🖨 Print
              </button>
              <button onClick={() => handleWhatsApp(false)} style={styles.btnWhatsapp}>
                📱 WhatsApp
              </button>
              <button onClick={() => handleWhatsApp(true)} style={styles.btnWhatsappBusiness}>
                💼 WhatsApp Business
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading timetables...</p>
          </div>
        )}

        {/* Timetable Display */}
        {selectedClass && classTimetable && foundEntries > 0 && !loading && (
          <div id="class-print" style={styles.timetableDisplay}>
            <div style={styles.timetableHeader}>
              <h2 style={styles.schoolName}>MIS INTERNATIONAL SCHOOL</h2>
              <h3 style={styles.timetableTitle}>CLASS TIMETABLE</h3>
              <p style={styles.classNameTitle}>Class: {getDisplayClassName(selectedClass)}</p>
              <p style={styles.periodInfo}>Weekly Timetable</p>
            </div>

            <div style={styles.timetableGrid}>
              <div style={styles.gridHeader}>
                <div style={styles.gridDayHeader}>Day</div>
                {standardPeriods.map(p => (
                  <div key={p} style={styles.gridPeriodHeader}>{p}</div>
                ))}
              </div>

              {days.map(day => {
                const dayData = classTimetable[day];
                return (
                  <div key={day} style={styles.gridRow}>
                    <div style={styles.gridDayLabel}>{day}</div>
                    {standardPeriods.map((period, periodIndex) => {
                      const content = getCellContent(day, periodIndex);
                      return (
                        <div key={period} style={{...styles.gridCell, ...(content ? styles.occupied : styles.free)}}>
                          {content ? (
                            <div style={styles.cellContent}>
                              <div style={styles.cellSubject}>{content.subject}</div>
                              <div style={styles.cellTeacher}>{content.teacher}</div>
                            </div>
                          ) : (
                            <div style={styles.cellFree}>Free</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div style={styles.timetableFooter}>
              <p>Generated by Timetable Management System</p>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {selectedClass && foundEntries === 0 && !loading && (
          <div style={styles.noDataMessage}>
            <div style={styles.noDataIcon}>📋</div>
            <h3>No Timetable Found</h3>
            <p>No timetable entries found for <strong>{getDisplayClassName(selectedClass)}</strong></p>
            <p style={styles.noDataHint}>Please create a timetable for this class first using the Timetable Editor.</p>
          </div>
        )}

        {/* Select Class Prompt */}
        {!selectedClass && !loading && (
          <div style={styles.selectPrompt}>
            <div style={styles.selectPromptIcon}>📚</div>
            <h3>Select a Class</h3>
            <p>Choose a class from the dropdown above to view its timetable</p>
            {(!Array.isArray(classes) || classes.length === 0) && (
              <p style={styles.noClassesHint}>
                No classes with timetables found. Please create timetables first.
              </p>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>Powered by Jauhari WebTech</p>
        <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f7fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
  },

  // Header
  header: {
    background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 100%)',
    color: 'white',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  headerTitle: {
    fontSize: '18px',
    margin: 0,
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '8px 18px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },

  // Main Content
  mainContent: {
    flex: 1,
    padding: '15px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Controls
  controls: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  selectorGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '15px',
  },
  label: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '14px',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    background: 'white',
    WebkitAppearance: 'none',
    appearance: 'none',
  },
  debugInfo: {
    padding: '10px 14px',
    background: '#f8f9fa',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#666',
    marginBottom: '15px',
    borderLeft: '3px solid #2d1b4e',
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  btnPrint: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#2c3e50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  btnWhatsapp: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#25D366',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  btnWhatsappBusiness: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#075E54',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #2d1b4e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  // Timetable Display
  timetableDisplay: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflowX: 'auto',
  },
  timetableHeader: {
    textAlign: 'center',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
    marginBottom: '15px',
  },
  schoolName: {
    margin: 0,
    fontSize: '20px',
    color: '#2c3e50',
  },
  timetableTitle: {
    margin: '4px 0',
    fontSize: '16px',
    color: '#7f8c8d',
  },
  classNameTitle: {
    margin: '4px 0 0 0',
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
  },
  periodInfo: {
    margin: '2px 0 0 0',
    fontSize: '12px',
    color: '#95a5a6',
  },
  timetableGrid: {
    minWidth: '700px',
  },
  gridHeader: {
    display: 'grid',
    gridTemplateColumns: '80px repeat(7, 1fr)',
    background: '#2d1b4e',
    color: 'white',
    borderRadius: '8px 8px 0 0',
  },
  gridDayHeader: {
    padding: '10px',
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '13px',
  },
  gridPeriodHeader: {
    padding: '10px',
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '11px',
  },
  gridRow: {
    display: 'grid',
    gridTemplateColumns: '80px repeat(7, 1fr)',
    borderBottom: '1px solid #e0e0e0',
  },
  gridDayLabel: {
    padding: '10px 8px',
    fontWeight: 600,
    color: '#2c3e50',
    textAlign: 'center',
    background: '#f8f9fa',
    fontSize: '12px',
  },
  gridCell: {
    padding: '8px 4px',
    minHeight: '70px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRight: '1px solid #f0f0f0',
  },
  occupied: {
    background: '#f0f4ff',
  },
  free: {
    background: '#fafafa',
  },
  cellContent: {
    fontSize: '10px',
    lineHeight: '1.3',
  },
  cellSubject: {
    fontWeight: 700,
    color: '#2c3e50',
    fontSize: '11px',
    marginBottom: '2px',
  },
  cellTeacher: {
    color: '#2d1b4e',
    fontSize: '9px',
    fontWeight: 500,
  },
  cellFree: {
    color: '#bdc3c7',
    fontSize: '11px',
    fontStyle: 'italic',
  },
  timetableFooter: {
    textAlign: 'center',
    paddingTop: '15px',
    borderTop: '2px solid #f0f0f0',
    marginTop: '15px',
  },

  // No Data Message
  noDataMessage: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px 20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  noDataIcon: {
    fontSize: '48px',
    marginBottom: '10px',
  },
  noDataHint: {
    fontSize: '13px',
    color: '#95a5a6',
    marginTop: '10px',
  },
  noClassesHint: {
    fontSize: '13px',
    color: '#ef4444',
    marginTop: '10px',
  },

  // Select Prompt
  selectPrompt: {
    background: 'white',
    borderRadius: '12px',
    padding: '60px 20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  selectPromptIcon: {
    fontSize: '64px',
    marginBottom: '15px',
  },

  // Footer
  footer: {
    background: 'white',
    textAlign: 'center',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
    marginTop: '20px',
  },
};

// Add keyframe animations and print styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media print {
    body * {
      visibility: hidden;
    }
    #class-print, #class-print * {
      visibility: visible;
    }
    #class-print {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 20px !important;
    }
    .grid-header {
      background: #2d1b4e !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .occupied {
      background: #f0f4ff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .grid-cell {
      min-height: 45px !important;
      padding: 4px !important;
    }
    .cell-content {
      font-size: 8px !important;
    }
    .cell-subject {
      font-size: 9px !important;
    }
    .cell-teacher {
      font-size: 7px !important;
    }
    .grid-day-label {
      font-size: 10px !important;
      padding: 6px !important;
    }
    .school-name {
      font-size: 16px !important;
    }
    .timetable-title {
      font-size: 12px !important;
    }
    .class-name-title {
      font-size: 12px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default ClassTimetable;